import React, { createContext, useContext, useEffect, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';

import { registerServiceWorker } from '../serviceWorkerRegistration';

const PwaContext = createContext({
  canInstall: false,
  installApp: async () => false,
});

let deferredInstallPrompt = null;
const installPromptListeners = new Set();

const notifyInstallPromptListeners = () => {
  installPromptListeners.forEach((listener) => listener(Boolean(deferredInstallPrompt)));
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    notifyInstallPromptListeners();
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    notifyInstallPromptListeners();
  });
}

export function PwaProvider({ children }) {
  const [canInstall, setCanInstall] = useState(Boolean(deferredInstallPrompt));
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);

  useEffect(() => {
    installPromptListeners.add(setCanInstall);
    setCanInstall(Boolean(deferredInstallPrompt));
    return () => installPromptListeners.delete(setCanInstall);
  }, []);

  useEffect(() => {
    registerServiceWorker({ onUpdate: setWaitingWorker });
  }, []);

  const installApp = async () => {
    const prompt = deferredInstallPrompt;
    if (!prompt) return false;

    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      return choice.outcome === 'accepted';
    } finally {
      deferredInstallPrompt = null;
      notifyInstallPromptListeners();
    }
  };

  const applyUpdate = () => {
    if (!waitingWorker) return;
    setIsApplyingUpdate(true);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => window.location.reload(),
      { once: true }
    );
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  };

  const dismissUpdate = () => setWaitingWorker(null);

  return (
    <PwaContext.Provider value={{ canInstall, installApp }}>
      {children}
      <Snackbar
        open={Boolean(waitingWorker)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: { xs: 1, sm: 2 } }}
      >
        <Alert
          severity="info"
          variant="outlined"
          onClose={dismissUpdate}
          sx={{
            width: '100%',
            alignItems: 'center',
            borderRadius: 3,
            background: 'var(--surface-bg, #ffffff)',
            borderColor: 'rgba(52, 120, 246, 0.28)',
            boxShadow: '0 16px 36px rgba(15, 23, 42, 0.12)',
          }}
          action={(
            <>
              <Button
                color="inherit"
                size="small"
                onClick={dismissUpdate}
                sx={{ fontWeight: 600, whiteSpace: 'nowrap', mr: 0.5 }}
              >
                Позже
              </Button>
              <Button
                color="primary"
                size="small"
                variant="contained"
                disabled={isApplyingUpdate}
                onClick={applyUpdate}
                sx={{ fontWeight: 700, whiteSpace: 'nowrap', borderRadius: 2 }}
              >
                {isApplyingUpdate ? 'Обновление…' : 'Обновить'}
              </Button>
            </>
          )}
        >
          Доступна новая версия приложения
        </Alert>
      </Snackbar>
    </PwaContext.Provider>
  );
}

export const usePwa = () => useContext(PwaContext);
